import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Inner scroller of the authenticated app shell (`.App main` owns the scroll
 * there, while marketing pages scroll the document itself).
 */
const APP_SCROLLER_SELECTOR = ".App main";

/**
 * Resets the scroll position on every forward navigation.
 *
 * React Router keeps the DOM scroller alive across route changes, so landing
 * on a new page from halfway down the previous one leaves the visitor in the
 * middle of the new page. Mounted once in `AppRouter` so it covers both
 * `I18nRouter` and `MonoRouter`.
 *
 * Three navigations are deliberately left alone:
 * - `POP` (back / forward), where the browser restores the previous position
 * - hash navigations, where the target section owns the scroll
 * - query-string-only changes, which stay on the page already being read
 */
const useScrollToTop = () => {
  const { key, pathname, search, hash } = useLocation();
  const navigationType = useNavigationType();
  const lastLocationRef = useRef<{ pathname: string; search: string } | null>(
    null,
  );

  useLayoutEffect(() => {
    const previousLocation = lastLocationRef.current;
    lastLocationRef.current = { pathname, search };

    if (navigationType === "POP" || hash) {
      return;
    }

    // Same page, different query string: event tabs, filters and pagination
    // all navigate that way, and throwing the visitor back to the top of the
    // page they are already reading is never what they asked for.
    if (
      previousLocation?.pathname === pathname &&
      previousLocation.search !== search
    ) {
      return;
    }

    // `instant` matters: `html { scroll-behavior: smooth }` would otherwise
    // animate the reset over several hundred milliseconds.
    const options: ScrollToOptions = { top: 0, left: 0, behavior: "instant" };

    window.scrollTo(options);
    document
      .querySelector<HTMLElement>(APP_SCROLLER_SELECTOR)
      ?.scrollTo(options);
  }, [key, pathname, search, hash, navigationType]);
};

export default useScrollToTop;
